import { useState } from "react";
import { Button, TextField, Typography, Box, Grid, Card, CardContent } from "@mui/material";
import { StarRating } from "./StarRating";
import { MessageSquare, Bug, Lightbulb } from "lucide-react";
import { useNotifications } from "./common/NotificationSystem";

type RequestType = "bug" | "feature" | "feedback" | null;

interface FormData {
  requestType: RequestType;
  // Bug fields
  severity: string;
  bugDescription: string;
  // Feature Request fields
  category: 'rules' | 'services' | 'general' | '';
  subcategory: string;
  awsService: string;
  resourceType: string;
  useCase: string;
  useCaseDescription: string;
  // Feedback fields
  rating: number;
  experience: string;
}

interface SupportFormProps {
  onClose?: () => void;
}

export function SupportForm({ onClose }: SupportFormProps) {
  const { showNotification } = useNotifications();
  const [formData, setFormData] = useState<FormData>({
    requestType: null,
    severity: "",
    bugDescription: "",
    category: "",
    subcategory: "",
    awsService: "",
    resourceType: "",
    useCase: "",
    useCaseDescription: "",
    rating: 0,
    experience: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return; // Prevent double submission
    
    // Validation
    if (!formData.requestType) {
      showNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please select a request type'
      });
      return;
    }

    if (formData.requestType === "bug") {
      if (!formData.severity || !formData.bugDescription) {
        showNotification({
          type: 'error',
          title: 'Validation Error',
          message: 'Please fill in all required fields'
        });
        return;
      }
    } else if (formData.requestType === "feature") {
      if (!formData.category || !formData.subcategory) {
        showNotification({
          type: 'error',
          title: 'Validation Error',
          message: 'Please select a category and subcategory'
        });
        return;
      }
      
      // For new service requests, validate service-specific fields
      if (formData.category === 'services' && formData.subcategory === 'new_service') {
        if (!formData.awsService || !formData.resourceType || !formData.useCaseDescription) {
          showNotification({
            type: 'error',
            title: 'Validation Error',
            message: 'Please fill in all required service fields'
          });
          return;
        }
      } else {
        // For other categories, validate general description
        if (!formData.useCase) {
          showNotification({
            type: 'error',
            title: 'Validation Error',
            message: 'Please provide a feature description'
          });
        return;
        }
      }
    } else if (formData.requestType === "feedback") {
      if (formData.rating === 0 || !formData.experience) {
        showNotification({
          type: 'error',
          title: 'Validation Error',
          message: 'Please provide a rating and share your experience'
        });
        return;
      }
    }

    try {
      setIsSubmitting(true);
      
      // Prepare the payload
      const payload = {
        requestType: formData.requestType,
        ...(formData.requestType === "bug" && {
          severity: formData.severity,
          bugDescription: formData.bugDescription,
        }),
        ...(formData.requestType === "feature" && {
          category: formData.category,
          subcategory: formData.subcategory,
          ...(formData.category === 'services' && formData.subcategory === 'new_service' && {
            awsService: formData.awsService,
            resourceType: formData.resourceType,
            useCaseDescription: formData.useCaseDescription,
          }),
          ...(formData.category !== 'services' || formData.subcategory !== 'new_service') && {
            useCase: formData.useCase,
          },
        }),
        ...(formData.requestType === "feedback" && {
          rating: formData.rating,
          experience: formData.experience,
        }),
      };

      // Get the API base URL from environment or use default
      const apiBaseUrl = process.env.REACT_APP_API_GATEWAY_URL;
      
      // Get the authorization token using the same pattern as tagopsService.ts
      const idToken = localStorage.getItem('id_token');
      const accessToken = localStorage.getItem('access_token');
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header using ID token (preferred) or access token (fallback)
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      } else if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      
      const response = await fetch(`${apiBaseUrl}/submit-form`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Form submitted successfully:", result);
      console.log("Response status:", response.status);
      console.log("Response message:", result.message);
      
      // Show success message based on response
      if (response.status === 200 && result.message) {
        console.log("Showing success notification with backend message:", result.message);
        try {
          showNotification({
            type: 'success',
            title: 'Success!',
            message: result.message
          });
          console.log("Success notification called successfully");
        } catch (error) {
          console.error("Error showing success notification:", error);
        }
      } else {
        console.log("Showing fallback success notification");
        try {
          showNotification({
            type: 'success',
            title: 'Success!',
            message: 'Thank you! Your submission has been received.'
          });
          console.log("Fallback success notification called successfully");
        } catch (error) {
          console.error("Error showing fallback success notification:", error);
        }
      }
    
    // Reset form
    setFormData({
      requestType: null,
      severity: "",
      bugDescription: "",
      category: "",
      subcategory: "",
      awsService: "",
      resourceType: "",
      useCase: "",
      useCaseDescription: "",
      rating: 0,
      experience: "",
    });
    
    // Close dialog
    if (onClose) {
      setTimeout(() => onClose(), 1000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showNotification({
        type: 'error',
        title: 'Submission Failed',
        message: 'Failed to submit form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetConditionalFields = () => {
    setFormData(prev => ({
      ...prev,
      severity: "",
      bugDescription: "",
      awsService: "",
      resourceType: "",
      useCase: "",
      rating: 0,
      experience: "",
    }));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography color="primary" sx={{ fontSize: '1rem' }}>
        We'd love to hear from you. Please select the nature of your request below.
      </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Request Type Selection */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" color="primary" sx={{ fontSize: '1.125rem', fontWeight: 500 }}>
            What is the nature of your request? *
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: 2,
                  borderColor: formData.requestType === "bug" ? "primary.main" : "divider",
                  bgcolor: formData.requestType === "bug" ? "action.hover" : "background.paper",
                  opacity: 1,
                  '&:hover': {
                    borderColor: "primary.main",
                    boxShadow: 2,
                  },
                  transition: 'all 0.3s ease',
                  boxShadow: formData.requestType === "bug" ? 2 : 0,
                }}
                onClick={() => {
                  setFormData(prev => ({ ...prev, requestType: "bug" }));
                  resetConditionalFields();
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Bug style={{ width: 24, height: 24, margin: '0 auto 8px', color: formData.requestType === "bug" ? '#1976d2' : '#666' }} />
                  <Typography color={formData.requestType === "bug" ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                    Bug Report
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: 2,
                  borderColor: formData.requestType === "feature" ? "primary.main" : "divider",
                  bgcolor: formData.requestType === "feature" ? "action.hover" : "background.paper",
                  opacity: 1,
                  '&:hover': {
                    borderColor: "primary.main",
                    boxShadow: 2,
                  },
                  transition: 'all 0.3s ease',
                  boxShadow: formData.requestType === "feature" ? 2 : 0,
                }}
                onClick={() => {
                  setFormData(prev => ({ ...prev, requestType: "feature" }));
                  resetConditionalFields();
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Lightbulb style={{ width: 24, height: 24, margin: '0 auto 8px', color: formData.requestType === "feature" ? '#1976d2' : '#666' }} />
                  <Typography color={formData.requestType === "feature" ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                    Feature Request
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                  sx={{
                    cursor: 'pointer',
                    border: 2,
                  borderColor: formData.requestType === "feedback" ? "primary.main" : "divider",
                  bgcolor: formData.requestType === "feedback" ? "action.hover" : "background.paper",
                  opacity: 1,
                    '&:hover': {
                    borderColor: "primary.main",
                    boxShadow: 2,
                  },
                  transition: 'all 0.3s ease',
                  boxShadow: formData.requestType === "feedback" ? 2 : 0,
                  }}
                  onClick={() => {
                  setFormData(prev => ({ ...prev, requestType: "feedback" }));
                    resetConditionalFields();
                  }}
                >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <MessageSquare style={{ width: 24, height: 24, margin: '0 auto 8px', color: formData.requestType === "feedback" ? '#1976d2' : '#666' }} />
                  <Typography color={formData.requestType === "feedback" ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                    Feedback
                    </Typography>
                </CardContent>
              </Card>
              </Grid>
          </Grid>
        </Box>

        {/* Bug Report Fields */}
        {formData.requestType === "bug" && (
          <Box
            className="fadeIn"
            sx={{
              p: 3,
              bgcolor: 'action.hover',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography color="primary" sx={{ fontWeight: 500 }}>
                Severity *
                </Typography>
              <TextField
                select
                    value={formData.severity}
                onChange={(e) =>
                  setFormData({ ...formData, severity: e.target.value })
                }
                SelectProps={{
                  native: true,
                }}
                    required
                fullWidth
                    sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                      },
                    }}
                  >
                <option value="">Select severity level</option>
                <option value="low">Low - Minor issue, workaround available</option>
                <option value="medium">Medium - Affects functionality</option>
                <option value="high">High - Major functionality impacted</option>
                <option value="critical">Critical - System is unusable</option>
              </TextField>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Typography color="primary" sx={{ fontWeight: 500 }}>
                Description *
              </Typography>
                <TextField
                  multiline
                  rows={6}
                  placeholder="Please describe the bug in detail. Include steps to reproduce, expected behavior, and actual behavior..."
                  value={formData.bugDescription}
                onChange={(e) =>
                  setFormData({ ...formData, bugDescription: e.target.value })
                }
                  required
                fullWidth
                  sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* Feature Request Fields */}
        {formData.requestType === "feature" && (
          <Box
            className="fadeIn"
            sx={{
              p: 3,
              bgcolor: 'action.hover',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }}
          >
            {/* Category Selection */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography color="primary" sx={{ fontWeight: 500 }}>
                Category *
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Card
                    onClick={() => setFormData({ ...formData, category: 'rules', subcategory: '' })}
                    sx={{
                      cursor: 'pointer',
                      border: 2,
                      borderColor: formData.category === 'rules' ? 'primary.main' : 'divider',
                      bgcolor: formData.category === 'rules' ? 'action.hover' : 'background.paper',
                      boxShadow: formData.category === 'rules' ? 2 : 0,
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 1,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography color={formData.category === 'rules' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                        Rules
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card
                    onClick={() => setFormData({ ...formData, category: 'services', subcategory: '' })}
                    sx={{
                      cursor: 'pointer',
                      border: 2,
                      borderColor: formData.category === 'services' ? 'primary.main' : 'divider',
                      bgcolor: formData.category === 'services' ? 'action.hover' : 'background.paper',
                      boxShadow: formData.category === 'services' ? 2 : 0,
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 1,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography color={formData.category === 'services' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                        Services
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card
                    onClick={() => setFormData({ ...formData, category: 'general', subcategory: '' })}
                    sx={{
                      cursor: 'pointer',
                      border: 2,
                      borderColor: formData.category === 'general' ? 'primary.main' : 'divider',
                      bgcolor: formData.category === 'general' ? 'action.hover' : 'background.paper',
                      boxShadow: formData.category === 'general' ? 2 : 0,
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 1,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography color={formData.category === 'general' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                        General
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Dynamic Fields Based on Category */}
            {formData.category === 'services' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                <Typography color="primary" sx={{ fontWeight: 500 }}>
                  What would you like to request?
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'new_service' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'new_service' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'new_service' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'new_service' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'new_service' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          I would like to request support for a new service/resource type
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'enhancement' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'enhancement' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'enhancement' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'enhancement' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'enhancement' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          I would like to enhance an existing service/resource type
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'integration' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'integration' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'integration' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'integration' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'integration' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          I need better integration with existing AWS services
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {formData.category === 'rules' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                <Typography color="primary" sx={{ fontWeight: 500 }}>
                  What type of rule feature would you like?
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'custom_rule' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'custom_rule' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'custom_rule' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'custom_rule' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'custom_rule' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          Create custom tagging rules
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'rule_templates' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'rule_templates' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'rule_templates' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'rule_templates' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'rule_templates' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          Add rule templates and presets
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'rule_scheduling' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'rule_scheduling' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'rule_scheduling' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'rule_scheduling' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'rule_scheduling' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          Advanced rule scheduling and automation
                        </Typography>
              </CardContent>
            </Card>
                  </Grid>
                </Grid>
          </Box>
            )}

            {formData.category === 'general' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                <Typography color="primary" sx={{ fontWeight: 500 }}>
                  What general feature would you like?
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'ui_improvement' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'ui_improvement' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'ui_improvement' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'ui_improvement' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'ui_improvement' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          User interface improvements
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'performance' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'performance' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'performance' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'performance' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'performance' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          Performance optimizations
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card
                      onClick={() => setFormData({ ...formData, subcategory: 'export_import' })}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: formData.subcategory === 'export_import' ? 'primary.main' : 'divider',
                        bgcolor: formData.subcategory === 'export_import' ? 'action.hover' : 'background.paper',
                        boxShadow: formData.subcategory === 'export_import' ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography color={formData.subcategory === 'export_import' ? "primary" : "text.primary"} sx={{ fontWeight: 500 }}>
                          Data export/import functionality
                </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Service-specific fields */}
            {formData.category === 'services' && formData.subcategory === 'new_service' && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                  <Typography color="primary" sx={{ fontWeight: 500 }}>
                    AWS Service *
                  </Typography>
                  <TextField
                    placeholder="e.g., EC2, S3, Lambda, RDS, etc."
                    value={formData.awsService}
                    onChange={(e) =>
                      setFormData({ ...formData, awsService: e.target.value })
                    }
                    required
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Typography color="primary" sx={{ fontWeight: 500 }}>
                    Resource Types *
                  </Typography>
                  <TextField
                    placeholder="e.g., Instance, Bucket, Function, Database, etc."
                    value={formData.resourceType}
                    onChange={(e) =>
                      setFormData({ ...formData, resourceType: e.target.value })
                    }
                    required
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Typography color="primary" sx={{ fontWeight: 500 }}>
                    Use Case Description *
                  </Typography>
                  <TextField
                    multiline
                    rows={6}
                    placeholder="Please describe your use case and how this new service/resource type would benefit your workflow..."
                    value={formData.useCaseDescription}
                    onChange={(e) =>
                      setFormData({ ...formData, useCaseDescription: e.target.value })
                    }
                    required
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Box>
              </>
            )}

            {/* General description field for other categories */}
            {formData.category && formData.category !== 'services' && formData.subcategory && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                <Typography color="primary" sx={{ fontWeight: 500 }}>
                  Feature Description *
                </Typography>
                <TextField
                  multiline
                  rows={6}
                  placeholder="Please describe your feature request in detail..."
                  value={formData.useCase}
                  onChange={(e) =>
                    setFormData({ ...formData, useCase: e.target.value })
                  }
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Box>
            )}

            {/* General description field for services enhancement/integration */}
            {formData.category === 'services' && formData.subcategory && formData.subcategory !== 'new_service' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                <Typography color="primary" sx={{ fontWeight: 500 }}>
                  Feature Description *
                </Typography>
                <TextField
                  multiline
                  rows={6}
                  placeholder="Please describe your enhancement or integration request in detail..."
                  value={formData.useCase}
                  onChange={(e) =>
                    setFormData({ ...formData, useCase: e.target.value })
                  }
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Feedback Fields */}
        {formData.requestType === "feedback" && (
          <Box
            className="fadeIn"
            sx={{
              p: 3,
              bgcolor: 'action.hover',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography color="primary" sx={{ fontWeight: 500 }}>
                    How would you rate your experience? *
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StarRating
                      value={formData.rating}
                      onChange={(rating) => setFormData({ ...formData, rating })}
                    />
                    {formData.rating > 0 && (
                  <Typography color="primary" sx={{ fontSize: '1rem' }}>
                        {formData.rating} {formData.rating === 1 ? "star" : "stars"}
                      </Typography>
                    )}
                  </Box>
                </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Typography color="primary" sx={{ fontWeight: 500 }}>
                Share Your Experience *
              </Typography>
                <TextField
                  multiline
                  rows={6}
                  placeholder="We'd love to hear your thoughts, opinions, and feelings about your experience..."
                  value={formData.experience}
                onChange={(e) =>
                  setFormData({ ...formData, experience: e.target.value })
                }
                  required
                fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
            </Box>
          </Box>
        )}

        {/* Submit Button */}
        {formData.requestType && (
          <Box sx={{ pt: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              disabled={isSubmitting}
              sx={{ 
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'action.disabled',
                  color: 'action.disabled',
                },
                py: 1.5,
              }}
            >
              {isSubmitting 
                ? "Submitting..." 
                : `Submit ${formData.requestType === "bug" ? "Bug Report" : formData.requestType === "feature" ? "Feature Request" : "Feedback"}`
              }
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
